import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API } from "../lib/api";
import { useAuth, GRADES } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { toast } from "sonner";
import { Download, Lock, Trash2, X, Save, Check, Edit3, FileText } from "lucide-react";

const EMPTY_EDIT = { title: "", description: "", min_grade: "Probationer" };

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function LibraryPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/library");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-US", { day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEdit({ title: it.title, description: it.description, min_grade: it.min_grade });
    setConfirmRemoveId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdit(EMPTY_EDIT);
  };

  const saveEdit = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/library/${id}`, edit);
      setItems((cur) => cur.map((i) => (i.id === id ? data : i)));
      toast.success(t("library.saved_toast"));
      cancelEdit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/library/${id}`);
      setItems((cur) => cur.filter((i) => i.id !== id));
      toast.success(t("library.removed_toast"));
      setConfirmRemoveId(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadUrl = (id) => {
    const token = localStorage.getItem("magick_token") || "";
    return `${API}/library/${id}/download?auth=${encodeURIComponent(token)}`;
  };

  return (
    <main data-testid="library-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-12">
        <p className="label-small text-[#8B3A3A] mb-3">{t("library.eyebrow")}</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("library.heading")}</h1>
        <p className="text-[#4A4238] mt-3 max-w-2xl">{t("library.intro")}</p>
        {isAdmin && (
          <p className="text-xs italic text-[#7A7061] mt-4" data-testid="library-admin-hint">
            {t("library.admin_hint")}
          </p>
        )}
      </header>

      {loading ? (
        <p className="text-[#7A7061] italic">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#C1B49A] p-16 text-center">
          <p className="font-display italic text-3xl text-[#4A4238]">{t("library.empty_title")}</p>
          <p className="text-sm text-[#7A7061] mt-2">{t("library.empty_sub")}</p>
        </div>
      ) : (
        <ol className="border border-[#C1B49A]" data-testid="library-list">
          {items.map((it) => {
            const isEditing = editingId === it.id;
            const askingRemove = confirmRemoveId === it.id;
            const locked = it.locked;
            return (
              <li
                key={it.id}
                data-testid={`library-row-${it.id}`}
                className={`border-b border-[#C1B49A] last:border-b-0 px-6 py-6 transition-colors ${locked ? "bg-[#EAE0CA]/20 opacity-75" : "bg-[#EAE0CA]/40 hover:bg-[#EAE0CA]/70"}`}
              >
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="label-small block mb-2">{t("library.field_title")}</label>
                      <input data-testid={`library-edit-title-${it.id}`} className="input-etched" value={edit.title} onChange={(e) => setEdit((d) => ({ ...d, title: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label-small block mb-2">{t("library.field_description")}</label>
                      <textarea data-testid={`library-edit-desc-${it.id}`} className="textarea-parchment" rows={3} value={edit.description} onChange={(e) => setEdit((d) => ({ ...d, description: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label-small block mb-2">{t("library.field_min_grade")}</label>
                      <select
                        data-testid={`library-edit-grade-${it.id}`}
                        value={edit.min_grade}
                        onChange={(e) => setEdit((d) => ({ ...d, min_grade: e.target.value }))}
                        className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A]"
                      >
                        {GRADES.map((g) => (<option key={g} value={g}>{g}</option>))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={cancelEdit} className="btn-secondary inline-flex items-center gap-1.5 select-none"><X size={12} /> {t("library.cancel")}</button>
                      <button data-testid={`library-save-${it.id}`} type="button" onClick={() => saveEdit(it.id)} disabled={busy} className="btn-primary inline-flex items-center gap-1.5 select-none">
                        <Save size={12} /> {busy ? t("library.saving") : t("library.save_btn")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-1 hidden md:flex items-center justify-center">
                      {locked ? <Lock size={22} className="text-[#7A7061]" /> : <FileText size={22} className="text-[#8B3A3A]" />}
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      <h3 className="font-display text-2xl text-[#1A1918] leading-tight">{it.title}</h3>
                      {it.description && <p className="text-sm text-[#4A4238] mt-1 leading-relaxed">{it.description}</p>}
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A7061] font-mono mt-2">
                        {t("library.uploaded_by")} <span className="text-[#4A4238]">{it.uploaded_by_name}</span> · {formatDate(it.created_at)} · {fmtBytes(it.size)} · <span className={locked ? "text-[#7A7061]" : "text-[#8B3A3A]"}>{it.min_grade}+</span>
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end gap-2 flex-wrap">
                      {locked ? (
                        <span className="text-xs italic text-[#7A7061] inline-flex items-center gap-1.5" data-testid={`library-locked-hint-${it.id}`}>
                          <Lock size={12} /> {t("library.locked_hint", { grade: it.min_grade })}
                        </span>
                      ) : (
                        <a
                          href={downloadUrl(it.id)}
                          download={it.original_filename}
                          data-testid={`library-download-${it.id}`}
                          className="btn-primary inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                        >
                          <Download size={12} /> {t("library.download_btn")}
                        </a>
                      )}
                      {isAdmin && (
                        <>
                          <button data-testid={`library-edit-${it.id}`} type="button" onClick={() => startEdit(it)} className="btn-ghost inline-flex items-center gap-1.5 select-none whitespace-nowrap">
                            <Edit3 size={12} /> {t("library.edit_btn")}
                          </button>
                          {askingRemove ? (
                            <div className="flex items-center gap-1">
                              <button
                                data-testid={`library-confirm-remove-${it.id}`}
                                type="button"
                                onClick={() => remove(it.id)}
                                disabled={busy}
                                className="btn-primary inline-flex items-center gap-1.5 select-none"
                                style={{ padding: "8px 12px", fontSize: "11px" }}
                              >
                                <Check size={12} /> {t("admin.confirm_remove")}
                              </button>
                              <button
                                data-testid={`library-cancel-remove-${it.id}`}
                                type="button"
                                onClick={() => setConfirmRemoveId(null)}
                                className="btn-ghost select-none"
                                style={{ padding: "8px 8px" }}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <button
                              data-testid={`library-remove-${it.id}`}
                              type="button"
                              onClick={() => setConfirmRemoveId(it.id)}
                              className="btn-ghost inline-flex items-center gap-1.5 text-[#8B3A3A] select-none whitespace-nowrap"
                            >
                              <Trash2 size={12} /> {t("library.remove_btn")}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </main>
  );
}
